import { useRef, useEffect, type ReactNode } from 'react'
import { Bell } from 'lucide-react'
import styles from './NotificationBell.module.css'

export interface NotificationBellProps {
  unreadCount: number
  open: boolean
  onToggle: () => void
  onClose: () => void
  children?: ReactNode
  className?: string
}

export function NotificationBell({
  unreadCount,
  open,
  onToggle,
  onClose,
  children,
  className,
}: NotificationBellProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, onClose])

  const displayCount = unreadCount > 99 ? '99+' : String(unreadCount)

  return (
    <div
      className={[styles.wrapper, className].filter(Boolean).join(' ')}
      ref={ref}
    >
      <button
        className={`${styles.bellBtn} ${open ? styles.bellBtnActive : ''}`}
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className={styles.badge}>{displayCount}</span>
        )}
      </button>
      {open && children}
    </div>
  )
}
